import { useState, useEffect, useRef } from 'react';
import {
  getFormEmbedBaseUrl,
  getLeadFormForDevelopment,
  getNewsletterForm,
  getGeneralLeadForm,
  type LeadFormRef,
} from '../services/leadFormService';
import { captureAttribution } from '../utils/attribution';

import { trackLeadConversion } from '../utils/analytics';
interface LeadFormEmbedProps {
  /** Development ID — finds the lead form linked to this property */
  developmentId?: string;
  /** Form type to look up when no developmentId is given */
  formType?: 'lead_form' | 'newsletter_form';
  /** Direct formId override — skips Firestore lookup */
  formId?: string;
  /** Minimum iframe height in px */
  minHeight?: number;
  /** Optional CSS class for the wrapper */
  className?: string;
  /** Fallback text when no form is found */
  fallbackText?: string;
}

export default function LeadFormEmbed({
  developmentId,
  formType = 'lead_form',
  formId: directFormId,
  minHeight = 500,
  className = '',
  fallbackText,
}: LeadFormEmbedProps) {
  const [formRef, setFormRef] = useState<LeadFormRef | null>(null);
  const [loading, setLoading] = useState(!directFormId);
  const [iframeHeight, setIframeHeight] = useState(minHeight);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Embed URL is built CLIENT-SIDE ONLY (useEffect). It was previously
  // computed during render, so prerendering baked the prerender server's
  // own origin (http://127.0.0.1:<port>) into the static HTML as page_url —
  // shipping a localhost URL to production and junking attribution.
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);

  // Resolve the formId from Firestore if not provided directly
  useEffect(() => {
    if (directFormId) {
      setFormRef({ formId: directFormId, name: '' });
      setLoading(false);
      return;
    }

    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      let result: LeadFormRef | null = null;

      if (developmentId) {
        result = await getLeadFormForDevelopment(developmentId);
        // Fall back to general lead form if no development-specific form exists
        if (!result) {
          result = await getGeneralLeadForm();
        }
      } else if (formType === 'newsletter_form') {
        result = await getNewsletterForm();
      } else {
        result = await getGeneralLeadForm();
      }

      if (!cancelled) {
        setFormRef(result);
        setLoading(false);
      }
    };

    lookup();
    return () => { cancelled = true; };
  }, [directFormId, developmentId, formType]);

  // Build the embed URL in the BROWSER only. Attribution (UTMs, referrer,
  // page_url) must reflect the real visitor's page — never the prerender
  // environment. During SSG this effect never runs, so the static HTML
  // carries the loading placeholder instead of a baked URL.
  useEffect(() => {
    if (!formRef?.formId) {
      setEmbedUrl(null);
      return;
    }
    const baseUrl = getFormEmbedBaseUrl();
    // Pass attribution data (UTMs, gclid, fbclid) to the iframe via URL
    // params. The iframe is cross-origin so it can't access our
    // sessionStorage directly. PublicLeadForm.tsx reads these params and
    // includes them in the form submission.
    const attr = captureAttribution();
    const attrParams = new URLSearchParams({ embed: 'true' });
    if (attr.utmSource)   attrParams.set('utm_source',   attr.utmSource);
    if (attr.utmMedium)   attrParams.set('utm_medium',   attr.utmMedium);
    if (attr.utmCampaign) attrParams.set('utm_campaign', attr.utmCampaign);
    if (attr.utmContent)  attrParams.set('utm_content',  attr.utmContent);
    if (attr.utmTerm)     attrParams.set('utm_term',     attr.utmTerm);
    if (attr.gclid)       attrParams.set('gclid',        attr.gclid);
    if (attr.fbclid)      attrParams.set('fbclid',       attr.fbclid);
    // Truncate referrer to avoid overly long URLs
    if (attr.referrer)    attrParams.set('referrer',     attr.referrer.substring(0, 200));
    // Landing page URL so the server can detect direct propinvest.co.za visits
    if (attr.url)         attrParams.set('page_url',     attr.url.split('?')[0]);
    setEmbedUrl(`${baseUrl}/form/${formRef.formId}?${attrParams.toString()}`);
  }, [formRef?.formId]);

  // Listen for postMessage from the embedded form: auto-resize, and the
  // success signal (PublicLeadForm posts { type: 'propcloud-submitted' }) —
  // that is where the Google Ads / GA4 lead conversion fires for embedded
  // lead forms. Newsletter sign-ups are not leads and are skipped.
  useEffect(() => {
    const formOrigin = new URL(getFormEmbedBaseUrl()).origin;
    const handleMessage = (event: MessageEvent) => {
      // Support both message types (form sends 'propcloud-resize')
      if ((event.data?.type === 'propcloud-resize' || event.data?.type === 'propcloud-form-resize') && event.data?.height) {
        setIframeHeight(Math.max(event.data.height, minHeight));
        return;
      }
      if (event.data?.type === 'propcloud-submitted' && event.origin === formOrigin && formType !== 'newsletter_form') {
        trackLeadConversion(
          developmentId ? 'embedded_development_form' : 'embedded_lead_form',
          developmentId ? { development_id: developmentId } : undefined,
        );
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [minHeight, formType, developmentId]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-card p-8 flex items-center justify-center ${className}`} style={{ minHeight }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!formRef) {
    // No form found — show fallback
    const defaultFallback = formType === 'newsletter_form'
      ? 'Newsletter signup coming soon.'
      : 'Enquiry form coming soon. Please contact us directly.';

    return (
      <div className={`bg-white rounded-lg shadow-card p-8 text-center ${className}`}>
        <p className="text-gray-500">{fallbackText || defaultFallback}</p>
      </div>
    );
  }

  // Client-only URL not built yet (prerender output always lands here).
  if (!embedUrl) {
    return (
      <div className={`bg-white rounded-lg shadow-card p-8 flex items-center justify-center ${className}`} style={{ minHeight }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden ${className}`} style={{ maxWidth: '100%' }}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={formRef.name || 'PropInvest&#8482; Form'}
        width="100%"
        height={iframeHeight}
        frameBorder="0"
        scrolling="no"
        style={{ border: 'none', overflow: 'hidden', minHeight, display: 'block' }}
        loading="lazy"
        allow="clipboard-write"
      />
    </div>
  );
}
